import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Copy, Download, ShieldCheck, KeyRound, FileText, CheckCircle2, XCircle, Plug } from 'lucide-react';

export default function ArcaConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cuit, setCuit] = useState('');
  const [razon, setRazon] = useState('');
  const [csr, setCsr] = useState('');
  const [genLoading, setGenLoading] = useState(false);
  const [certPem, setCertPem] = useState('');
  const [keyPem, setKeyPem] = useState('');
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testService, setTestService] = useState('ws_sr_constancia_inscripcion');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/arca/config');
      setConfig(res.data);
      setCuit(res.data.cuit_representada || '');
      setRazon(res.data.razon_social || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando configuración');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { (async () => {
    try { await api.post('/arca/activate-module'); } catch { /* ya existe o sin permiso */ }
    await load();
  })(); }, []);

  const guardarDatos = async () => {
    setSaving(true);
    try {
      await api.put('/arca/config', { cuit_representada: cuit, razon_social: razon, environment: 'produccion' });
      toast.success('Datos guardados');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const generarCsr = async () => {
    const c = (cuit || '').replace(/\D/g, '');
    if (c.length !== 11) { toast.error('Ingresá un CUIT válido (11 dígitos)'); return; }
    if (!razon.trim()) { toast.error('Ingresá la razón social'); return; }
    setGenLoading(true);
    try {
      const res = await api.post('/arca/certificado/generar', { cuit: c, razon_social: razon });
      setCsr(res.data.csr_pem);
      toast.success('CSR generado. Descargalo y subilo a ARCA.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error generando CSR');
    } finally {
      setGenLoading(false);
    }
  };

  const copiar = (txt) => { navigator.clipboard.writeText(txt); toast.success('Copiado'); };

  const descargarCsr = () => {
    const blob = new Blob([csr], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'solicitud.csr'; a.click();
    URL.revokeObjectURL(url);
  };

  const subirCert = async () => {
    if (!certPem.trim()) { toast.error('Pegá el contenido del certificado (.crt)'); return; }
    setUploading(true);
    try {
      const res = await api.post('/arca/certificado/subir', { cert_pem: certPem, key_pem: keyPem || null });
      toast.success('Certificado cargado correctamente');
      setCertPem(''); setKeyPem('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar el certificado');
    } finally {
      setUploading(false);
    }
  };

  const probar = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await api.post('/arca/test-conexion', null, { params: { service: testService } });
      setTestResult(res.data);
      if (res.data.success) toast.success('Conexión con WSAA OK'); else toast.error('Falló la conexión');
    } catch (e) {
      setTestResult({ success: false, error: e.response?.data?.detail || 'Error' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="p-6"><Loader2 className="animate-spin text-slate-400" /></div>;

  const cert = config?.cert_info;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="arca-config-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Configuración ARCA</h1>
        <p className="text-slate-500 text-sm mt-1">Ambiente: <Badge className="bg-sky-600">PRODUCCIÓN</Badge></p>
      </div>

      {/* Datos */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Datos del representado</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>CUIT representada (el que consulta)</Label>
            <Input data-testid="arca-cfg-cuit" value={cuit} onChange={(e) => setCuit(e.target.value)} className="font-mono mt-1" placeholder="20123456789" />
          </div>
          <div>
            <Label>Razón social</Label>
            <Input data-testid="arca-cfg-razon" value={razon} onChange={(e) => setRazon(e.target.value)} className="mt-1" placeholder="MI RAZON SOCIAL" />
          </div>
        </div>
        <Button className="mt-4" onClick={guardarDatos} disabled={saving} data-testid="arca-cfg-save">
          {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Guardar
        </Button>
      </Card>

      {/* Estado del certificado */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck size={18} /> Certificado digital</h2>
          {cert ? (
            <Badge className="bg-emerald-600" data-testid="arca-cert-ok">Cargado</Badge>
          ) : (
            <Badge variant="outline" className="text-amber-600 border-amber-300">Falta</Badge>
          )}
        </div>

        {cert && (
          <div className="mb-4 p-3 rounded-lg bg-slate-50 text-sm">
            <p><span className="text-slate-500">Titular:</span> {cert.subject_cn}</p>
            <p><span className="text-slate-500">Vence:</span> {cert.not_after ? new Date(cert.not_after).toLocaleString() : '—'}</p>
          </div>
        )}

        {/* Paso 1: Generar CSR */}
        <div className="border rounded-lg p-4 mb-4">
          <p className="font-medium text-slate-800 mb-1">Paso 1 — Generar solicitud (CSR)</p>
          <p className="text-sm text-slate-500 mb-3">Generamos la clave privada (queda cifrada acá) y el CSR para subir a ARCA. La clave nunca sale del servidor.</p>
          <Button variant="outline" onClick={generarCsr} disabled={genLoading} data-testid="arca-gen-csr">
            {genLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <KeyRound size={16} className="mr-2" />}
            Generar CSR
          </Button>
          {(csr || config?.has_csr) && (
            <div className="mt-3">
              {csr ? (
                <Textarea value={csr} readOnly rows={6} className="font-mono text-xs" data-testid="arca-csr-text" />
              ) : (
                <p className="text-xs text-slate-500">Ya hay un CSR generado. Volvé a generar si necesitás uno nuevo.</p>
              )}
              <div className="flex gap-2 mt-2">
                {csr && <Button size="sm" variant="outline" onClick={() => copiar(csr)}><Copy size={14} className="mr-1" /> Copiar</Button>}
                {csr && <Button size="sm" variant="outline" onClick={descargarCsr}><Download size={14} className="mr-1" /> Descargar .csr</Button>}
              </div>
            </div>
          )}
        </div>

        {/* Instrucciones ARCA */}
        <div className="border rounded-lg p-4 mb-4 bg-sky-50 border-sky-200">
          <p className="font-medium text-sky-900 mb-2">Paso 2 — En el portal de ARCA (con Clave Fiscal)</p>
          <ol className="text-sm text-sky-800 list-decimal pl-5 space-y-1">
            <li>Ingresá a <span className="font-mono">auth.afip.gob.ar</span> con tu CUIT y Clave Fiscal.</li>
            <li>Andá a <strong>Administración de Certificados Digitales</strong> → <strong>Agregar alias</strong> / Nuevo certificado.</li>
            <li>Pegá el contenido del CSR (o subí el archivo <span className="font-mono">solicitud.csr</span>) y descargá el certificado <span className="font-mono">.crt</span>.</li>
            <li>Andá a <strong>Administrador de Relaciones de Clave Fiscal</strong> → representado <strong>AFIP</strong> y adherí (asociando el <strong>mismo certificado</strong>) los siguientes servicios según lo que uses:
              <ul className="list-disc pl-5 mt-1 space-y-0.5">
                <li>Padrón: <span className="font-mono">ws_sr_constancia_inscripcion</span></li>
                <li>Apócrifos: <span className="font-mono">wsapoc</span></li>
                <li>Constatación de Comprobantes: <span className="font-mono">wscdc</span></li>
              </ul>
            </li>
          </ol>
        </div>

        {/* Paso 3: Subir CRT */}
        <div className="border rounded-lg p-4 mb-4">
          <p className="font-medium text-slate-800 mb-1">Paso 3 — Subir el certificado (.crt)</p>
          <p className="text-sm text-slate-500 mb-2">Pegá el contenido del certificado emitido por ARCA (PEM, empieza con <span className="font-mono">-----BEGIN CERTIFICATE-----</span>).</p>
          <Textarea value={certPem} onChange={(e) => setCertPem(e.target.value)} rows={5} className="font-mono text-xs" placeholder="-----BEGIN CERTIFICATE-----&#10;..." data-testid="arca-cert-text" />
          <details className="mt-2">
            <summary className="text-xs text-slate-500 cursor-pointer">¿Generaste la clave por fuera? Pegá también la clave privada (avanzado)</summary>
            <Textarea value={keyPem} onChange={(e) => setKeyPem(e.target.value)} rows={4} className="font-mono text-xs mt-2" placeholder="-----BEGIN PRIVATE KEY-----&#10;..." data-testid="arca-key-text" />
          </details>
          <Button className="mt-3" onClick={subirCert} disabled={uploading} data-testid="arca-upload-cert">
            {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileText size={16} className="mr-2" />}
            Cargar certificado
          </Button>
        </div>

        {/* Probar */}
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={testService} onValueChange={setTestService}>
            <SelectTrigger className="w-64" data-testid="arca-test-service">
              <SelectValue placeholder="Servicio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ws_sr_constancia_inscripcion">Padrón (constancia)</SelectItem>
              <SelectItem value="wsapoc">Apócrifos (wsapoc)</SelectItem>
              <SelectItem value="wscdc">Comprobantes (wscdc)</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={probar} disabled={testing || !cert} data-testid="arca-test-conn">
            {testing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plug size={16} className="mr-2" />}
            Probar conexión (WSAA)
          </Button>
          {testResult && (
            <span className={`flex items-center gap-1 text-sm ${testResult.success ? 'text-emerald-600' : 'text-red-600'}`} data-testid="arca-test-result">
              {testResult.success ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              {testResult.success ? 'Autenticación OK' : (testResult.error || 'Falló')}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}
